import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import {
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  SchoolOutlined,
  AutoStoriesOutlined,
  GroupsOutlined,
  CalendarMonthOutlined,
} from '@mui/icons-material';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/invalid-email': return 'Adresse email invalide';
      case 'auth/user-not-found': return 'Aucun compte trouvé avec cette adresse email';
      case 'auth/wrong-password': return 'Mot de passe incorrect';
      case 'auth/too-many-requests': return 'Trop de tentatives. Veuillez réessayer plus tard';
      case 'auth/network-request-failed': return 'Problème de connexion réseau';
      default: return `Une erreur s'est produite: ${error.message}`;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      setLoading(false);
      return;
    }
    try {
      const user = await login(email, password);
      if (user && user.role === 'admin') user.password = password;
      switch (user.role) {
        case 'superAdmin': navigate('/super-admin/dashboard'); break;
        case 'admin': navigate('/admin/dashboard'); break;
        case 'directionRH': navigate('/admin/dashboard'); break;
        case 'chefService': navigate('/chef-service/dashboard'); break;
        case 'personnel': navigate('/personnel/dashboard'); break;
        default:
          setError('Rôle utilisateur non reconnu');
          await logout();
      }
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setNotification({ open: true, message: 'Veuillez saisir votre adresse email', severity: 'error' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setNotification({ open: true, message: "Format d'email invalide", severity: 'error' });
      return;
    }
    setResetLoading(true);
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/reset-password',
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, resetEmail, actionCodeSettings);
      setNotification({
        open: true,
        message: `Lien de réinitialisation envoyé à ${resetEmail}. Vérifiez vos spams.`,
        severity: 'success'
      });
      setResetDialogOpen(false);
      setResetEmail('');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setNotification({
          open: true,
          message: `Si un compte existe avec ${resetEmail}, un email a été envoyé.`,
          severity: 'info'
        });
      } else {
        let msg = "Erreur lors de l'envoi de l'email";
        if (error.code === 'auth/too-many-requests') msg = 'Trop de tentatives. Réessayez dans quelques minutes';
        if (error.code === 'auth/network-request-failed') msg = 'Problème de connexion réseau';
        setNotification({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setResetLoading(false);
    }
  };

  const features = [
    { icon: <GroupsOutlined sx={{ fontSize: 22 }} />, label: 'Gestion du personnel' },
    { icon: <CalendarMonthOutlined sx={{ fontSize: 22 }} />, label: 'Emplois du temps' },
    { icon: <AutoStoriesOutlined sx={{ fontSize: 22 }} />, label: 'Suivi des absences' },
    { icon: <SchoolOutlined sx={{ fontSize: 22 }} />, label: 'Multi-établissements' },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', fontFamily: '"DM Sans", sans-serif' }}>
      {/* ── Panneau gauche ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '48%',
          minHeight: '100vh',
          background: 'linear-gradient(160deg, #0a3040 0%, #0f4c5c 45%, #1a6b80 100%)',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Cercles décoratifs */}
        <Box sx={{
          position: 'absolute', top: -80, right: -80, width: 360, height: 360,
          borderRadius: '50%', background: 'rgba(245,158,11,0.10)', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -120, left: -60, width: 420, height: 420,
          borderRadius: '50%', background: 'rgba(26,107,128,0.25)', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: '40%', right: -40, width: 200, height: 200,
          borderRadius: '50%', background: 'rgba(245,158,11,0.07)', pointerEvents: 'none',
        }} />
        {/* Grille de points */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        {/* Logo + nom */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px',
              background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(245,158,11,0.4)',
            }}>
              <SchoolOutlined sx={{ color: '#1a1a1a', fontSize: 26 }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.5rem',
              color: '#ffffff', letterSpacing: '-0.02em',
            }}>
              EduGestion
            </Typography>
          </Box>
          <Typography sx={{
            color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem',
            fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', ml: 0.5,
          }}>
            Plateforme de gestion scolaire
          </Typography>
        </Box>

        {/* Titre central */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{
            fontFamily: '"Outfit", sans-serif', fontWeight: 800,
            fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#ffffff',
            lineHeight: 1.15, mb: 2.5,
          }}>
            Gérez votre<br />
            établissement<br />
            <Box component="span" sx={{ color: '#f59e0b' }}>avec clarté.</Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 340 }}>
            Un outil complet pour administrer le personnel, les emplois du temps, les absences et la communication interne.
          </Typography>

          {/* Feature pills */}
          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {features.map((f, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                background: 'rgba(255,255,255,0.07)', borderRadius: '10px',
                px: 2, py: 1.2, width: 'fit-content',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
              }}>
                <Box sx={{ color: '#f59e0b' }}>{f.icon}</Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem', fontWeight: 500 }}>
                  {f.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer gauche */}
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', position: 'relative', zIndex: 1 }}>
          © 2026 EduGestion · Softmarket
        </Typography>
      </Box>

      {/* ── Panneau droit ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f0f4f8',
        p: { xs: 3, sm: 6 },
        position: 'relative',
      }}>
        {/* Logo mobile uniquement */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center', gap: 1.5, mb: 4,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: 'linear-gradient(135deg, #0f4c5c, #1a6b80)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SchoolOutlined sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.3rem', color: '#0f4c5c' }}>
            EduGestion
          </Typography>
        </Box>

        {/* Carte formulaire */}
        <Box sx={{
          width: '100%', maxWidth: 420,
          background: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(15,76,92,0.12), 0 4px 16px rgba(15,76,92,0.08)',
          p: { xs: 3.5, sm: 5 },
          border: '1px solid rgba(15,76,92,0.08)',
        }}>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{
              fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              fontSize: '1.75rem', color: '#0f4c5c', mb: 0.5, lineHeight: 1.2,
            }}>
              Bon retour 👋
            </Typography>
            <Typography sx={{ color: '#4a5568', fontSize: '0.9rem' }}>
              Connectez-vous à votre espace de gestion
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5, borderRadius: '10px', fontSize: '0.875rem',
                '& .MuiAlert-icon': { alignItems: 'center' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a2332', mb: 0.75, letterSpacing: '0.02em' }}>
                ADRESSE EMAIL
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="votre@email.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: '#0f4c5c', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    background: '#f8fafc',
                    '& fieldset': { borderColor: 'rgba(15,76,92,0.15)' },
                    '&:hover fieldset': { borderColor: '#0f4c5c' },
                    '&.Mui-focused fieldset': { borderColor: '#0f4c5c', borderWidth: 2 },
                  },
                  '& input': { py: 1.4, fontSize: '0.9rem' },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a2332', letterSpacing: '0.02em' }}>
                  MOT DE PASSE
                </Typography>
                <Link
                  component="button"
                  type="button"
                  onClick={() => { setResetEmail(email); setResetDialogOpen(true); }}
                  sx={{
                    fontSize: '0.78rem', color: '#0f4c5c', fontWeight: 500,
                    textDecoration: 'none', '&:hover': { color: '#f59e0b', textDecoration: 'underline' },
                    transition: 'color 0.2s',
                  }}
                >
                  Mot de passe oublié ?
                </Link>
              </Box>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="••••••••"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#0f4c5c', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#4a5568' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    background: '#f8fafc',
                    '& fieldset': { borderColor: 'rgba(15,76,92,0.15)' },
                    '&:hover fieldset': { borderColor: '#0f4c5c' },
                    '&.Mui-focused fieldset': { borderColor: '#0f4c5c', borderWidth: 2 },
                  },
                  '& input': { py: 1.4, fontSize: '0.9rem' },
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                height: 50,
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: '12px',
                background: loading
                  ? 'rgba(15,76,92,0.5)'
                  : 'linear-gradient(135deg, #0f4c5c 0%, #1a6b80 100%)',
                boxShadow: loading ? 'none' : '0 6px 20px rgba(15,76,92,0.35)',
                letterSpacing: '0.02em',
                '&:hover': {
                  background: 'linear-gradient(135deg, #093340 0%, #0f4c5c 100%)',
                  boxShadow: '0 8px 25px rgba(15,76,92,0.45)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
                transition: 'all 0.25s ease',
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Se connecter'}
            </Button>
          </Box>

          <Box sx={{
            mt: 3, pt: 3,
            borderTop: '1px solid rgba(15,76,92,0.08)',
            display: 'flex', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#4a5568' }}>
              Accès réservé au personnel autorisé
            </Typography>
          </Box>
        </Box>

        {/* Version badge */}
        <Typography sx={{ mt: 3, color: '#9aa5b4', fontSize: '0.75rem' }}>
          EduGestion 2026 · v1.0
        </Typography>
      </Box>

      {/* ── Dialog reset mot de passe ── */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: '#0f4c5c', pb: 0.5 }}>
          Réinitialiser le mot de passe
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: 0.5 }}>
            Saisissez votre adresse email pour recevoir un lien de réinitialisation.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Adresse email"
            type="email"
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ color: '#0f4c5c', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            disabled={resetLoading}
            sx={{ color: '#4a5568', borderRadius: '8px' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            disabled={resetLoading || !resetEmail}
            sx={{ borderRadius: '8px', px: 3 }}
          >
            {resetLoading ? <CircularProgress size={20} color="inherit" /> : 'Envoyer le lien'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
